import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

const MOBILE_WIDTH = 400;
const MOBILE_HEIGHT = 822;
const MOBILE_FIT_PADDING = 24;

const PreviewWrapper = ({ fullPageWidth = 1300, updateHeight = false, rounded = false, children }) => {
  const isMobile = false;

  const effectiveUpdateHeight = updateHeight && !isMobile;

  const wrapperRef = useRef(null);
  const childRef = useRef(null);
  const mobileSlotRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [transformOrigin, setTransformOrigin] = useState('top left');
  const [phoneFrameScale, setPhoneFrameScale] = useState(1);

  const calculateScale = useCallback(() => {
    if (wrapperRef.current && childRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth;
      const wrapperHeight = wrapperRef.current.offsetHeight;
      const childHeight = childRef.current.offsetHeight;

      const widthScale = wrapperWidth / fullPageWidth;
      const heightScale = childHeight > 0 ? wrapperHeight / childHeight : widthScale;
      const scaledHeight = childHeight * widthScale;

      if (isMobile) {
        setScale(Math.max(widthScale, heightScale));
      } else if (scaledHeight > wrapperHeight && !effectiveUpdateHeight) {
        setScale(heightScale);
      } else {
        setScale(widthScale);
      }
      setTransformOrigin(isMobile ? 'top left' : 'top center');
    }
  }, [effectiveUpdateHeight, fullPageWidth, isMobile]);

  useEffect(() => {
    calculateScale();
    const intervalId = setInterval(calculateScale, 200);
    return () => clearInterval(intervalId);
  }, [calculateScale]);

  useEffect(() => {
    if (!effectiveUpdateHeight) return;
    if (scale > 0 && childRef.current && wrapperRef.current) {
      const previewChild = childRef.current.firstChild;
      if (previewChild) {
        const childHeight = previewChild.offsetHeight;
        const scaledChildHeight = childHeight * scale;
        wrapperRef.current.style.height = `${scaledChildHeight}px`;
      }
    }
  }, [scale, effectiveUpdateHeight]);

  useLayoutEffect(() => {
    if (!isMobile) return undefined;

    const el = mobileSlotRef.current;
    if (!el) return undefined;

    const updateFrameScale = () => {
      const { width, height } = el.getBoundingClientRect();
      const availW = Math.max(0, width - MOBILE_FIT_PADDING);
      const availH = Math.max(0, height - MOBILE_FIT_PADDING);
      if (availW <= 0 || availH <= 0) {
        setPhoneFrameScale(1);
        return;
      }
      const s = Math.min(1, availW / MOBILE_WIDTH, availH / MOBILE_HEIGHT);
      setPhoneFrameScale(Number.isFinite(s) && s > 0 ? s : 1);
    };

    updateFrameScale();
    const ro = new ResizeObserver(updateFrameScale);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isMobile]);

  const scaledContentMobile = (
    <div
      ref={childRef}
      style={{
        transform: `scale(${scale})`,
        transformOrigin,
        padding: 0,
        width: `${MOBILE_WIDTH}px`,
        transition: 'transform 0.3s ease',
        position: 'absolute',
        top: 0,
        left: 0,
      }}
      className={`p-0 ${rounded && 'md:p-2'}`}
    >
      <div
        style={{
          ...(rounded && {
            borderRadius: rounded ? 8 : 0,
            overflow: 'hidden',
          }),
        }}
      >
        {children}
      </div>
    </div>
  );

  const scaledContentDesktop = (
    <div
      ref={childRef}
      style={{
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: `translateX(-50%) scale(${scale})`,
        transformOrigin: 'top center',
        width: `${fullPageWidth}px`,
        transition: 'transform 0.3s ease',
      }}
      className={`p-0 ${rounded && 'md:p-2'}`}
    >
      <div
        style={{
          boxSizing: 'border-box',
          border: '1px solid #FFFFFF',
          borderRadius: '10px',
          overflow: 'hidden',
          ...(rounded && {
            maxHeight: `calc(var(--preview-height))`
          }),
        }}
      >
        {children}
      </div>
    </div>
  );

  if (isMobile) {
    const layoutW = MOBILE_WIDTH * phoneFrameScale;
    const layoutH = MOBILE_HEIGHT * phoneFrameScale;

    return (
      <div className="bg-transparent relative flex w-full flex-1 min-h-0 flex-col items-center justify-center overflow-hidden">
        <div
          ref={mobileSlotRef}
          className="flex w-full flex-1 min-h-0 items-center justify-center overflow-hidden py-2"
        >
          <div
            className="shrink-0"
            style={{
              width: layoutW,
              height: layoutH,
              position: 'relative',
            }}
          >
            <div
              className="absolute left-1/2 top-1/2"
              style={{
                width: MOBILE_WIDTH,
                height: MOBILE_HEIGHT,
                transform: `translate(-50%, -50%) scale(${phoneFrameScale})`,
                transformOrigin: 'center center',
                boxSizing: 'border-box',
                border: '10px solid #000000',
                borderRadius: 48,
                overflow: 'hidden',
                boxShadow: '0 20px 50px rgba(0, 0, 0, 0.2)',
                background: 'linear-gradient(180deg, #E85A2A 0%, #F7E4A8 100%)',
              }}
            >
              <div
                ref={wrapperRef}
                className="bg-transparent h-full w-full relative flex justify-center items-center overflow-hidden"
              >
                {scaledContentMobile}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={`bg-transparent relative flex w-full justify-center overflow-hidden ${effectiveUpdateHeight ? 'shrink-0' : 'flex-1 min-h-0 min-h-56'}`}
    >
      {scaledContentDesktop}
    </div>
  );
};

export default PreviewWrapper;

PreviewWrapper.propTypes = {
  fullPageWidth: PropTypes.number,
  updateHeight: PropTypes.bool,
  rounded: PropTypes.bool,
  children: PropTypes.node,
};
